import React, { useEffect, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import ReactMarkdown from 'react-markdown'
import InterstitialLink from '@/components/InterstitialLink'
import { MarkdownModal, Text, Type } from '@/components/common'
import Image from '@/components/contentful/Image'
import { AngelFundingLogo } from '@/components/svg'
import { Campaign } from '@/services/api/campaign'
import useSiteAssets from '@/services/hooks/useSiteAssets'
import { getNextRouterQueryParam } from '@/utils'
import MenuItem from './MenuItem'

interface FooterProps {
  campaign?: Campaign
}

const Footer: React.FC<FooterProps> = ({ campaign }) => {
  const router = useRouter()
  const [modalContent, setModalContent] = useState<string | undefined>()
  const [modalHeading, setModalHeading] = useState<string | undefined>()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const siteAssets = useSiteAssets()
  const currentYear = new Date().getFullYear()

  const openModal = (content?: string, heading?: string) => {
    setModalContent(content)
    setModalHeading(heading)
    setIsModalOpen(true)
  }

  useEffect(() => {
    const value = getNextRouterQueryParam('education', router)
    if (value === 'true' && siteAssets.howCrowdfundingWorks) {
      openModal(siteAssets.howCrowdfundingWorks)
    }
  }, [router, siteAssets.howCrowdfundingWorks])

  return (
    <footer className="bg-black px-6 py-12 font-whitney text-xs text-white md:px-12 md:py-20">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:flex-wrap md:gap-x-14 md:gap-y-6">
          {siteAssets.footerLogo && (
            <Link href="/">
              <Image image={siteAssets.footerLogo} height={24} />
            </Link>
          )}
          {!siteAssets.footerLogo &&
            siteAssets.isWhiteLabel &&
            siteAssets.contentfulName && (
              <InterstitialLink domain="invest.angel.com">
                <Text as="p" preset="body.xs" className="mb-2">
                  Powered by
                </Text>
                <AngelFundingLogo
                  className="h-[24px]"
                  fill1="#fff"
                  fill2="#fff"
                />
              </InterstitialLink>
            )}
          {!siteAssets.footerLogo &&
            !siteAssets.isWhiteLabel &&
            siteAssets.contentfulName && (
              <Link href="/">
                <AngelFundingLogo
                  className="h-[24px]"
                  fill1="#fff"
                  fill2="#fff"
                />
              </Link>
            )}

          <menu className="inline-flex flex-wrap gap-x-6 gap-y-2 text-base md:mr-auto md:gap-x-10 ">
            <MenuItem
              label="Privacy"
              showItem={!!siteAssets?.privacy}
              onClick={() => openModal(siteAssets?.privacy, 'Privacy')}
            />
            <MenuItem
              label="Terms of Service"
              showItem={
                !!(
                  siteAssets?.termsOfServiceIntro || siteAssets?.termsOfService
                )
              }
              onClick={() =>
                openModal(
                  [
                    siteAssets?.termsOfServiceIntro,
                    siteAssets?.termsOfService,
                  ].join('\n'),
                  'Terms of Service'
                )
              }
            />
            <MenuItem
              label="How Crowdfunding Works"
              showItem={!!siteAssets?.howCrowdfundingWorks}
              onClick={() => openModal(siteAssets?.howCrowdfundingWorks)}
            />
            <MenuItem
              label="Electronic Consent"
              showItem={!!siteAssets?.electronicConsent}
              onClick={() => openModal(siteAssets?.electronicConsent)}
            />
            <MenuItem
              label="Portal Disclosures"
              showItem={!!siteAssets?.portalDisclosures}
              onClick={() => openModal(siteAssets?.portalDisclosures)}
            />
          </menu>

          <Type
            as="p"
            variant="caption-md"
            className="uppercase text-core-gray-400"
          >
            COPYRIGHT © {currentYear}, {siteAssets.copyrightName}. ALL RIGHTS
            RESERVED.
          </Type>
        </div>

        <hr className="my-6 border-core-gray-900 border-t border-b" />

        <div className="caption-sm mt-2 whitespace-pre-wrap break-words text-core-gray-500">
          <ReactMarkdown>
            {campaign?.issuerName && siteAssets.campaignFooterDisclaimer
              ? siteAssets.campaignFooterDisclaimer.replaceAll(
                  /{{issuerName}}/g,
                  campaign?.issuerName
                )
              : siteAssets?.footerDisclaimer}
          </ReactMarkdown>
        </div>
      </div>

      {modalContent && (
        <MarkdownModal
          isOpen={isModalOpen}
          setIsOpen={setIsModalOpen}
          info={modalContent}
          heading={modalHeading}
        />
      )}
    </footer>
  )
}

export default Footer
